/* FacilityForm.jsx — front-end form with Keycloak redirect (fetch API, no axios) */
import React, { useEffect, useState } from 'react';
import {
  Container, Typography, TextField, MenuItem, Button,
  Box, Paper, Grid, Alert,
} from '@mui/material';
import { initKeycloak } from '../keycloak';

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080';

const DEFAULT_EVAL_DAYS = 30;

export default function FacilityForm() {
  /* ---------- Keycloak ---------- */
  const [token, setToken] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const kc = await initKeycloak();
        if (!kc.authenticated) {
          kc.login();                // redirige si non connecté
          return;
        }
        setToken(kc.token ?? '');
      } catch (err) {
        console.error('❌ Keycloak init failed:', err);
      }
    })();
  }, []);

  /* ---------- Form state ---------- */
  const [formData, setFormData] = useState({
  name: '',
  address: '',
  lon: '0.0',
  lat: '0.0',
  type: 'cron',
  facility_type: 'other', // ⬅️ ajouté ici
  L: '',
  C: '',
  OU: '',
  emailAddress: '',
  current_registration_step: 'unstarted',
  password: '',
  evaluationDays: DEFAULT_EVAL_DAYS,
});


  const [error, setError]   = useState('');
  const [success, setSuccess] = useState('');

  /* ---------- Handlers ---------- */
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
    setSuccess('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!token) return;                    // sécurité

    try {
      const payload = {
        ...formData,
        lon: Number(formData.lon) || 0,
        lat: Number(formData.lat) || 0,
      };

      const res = await fetch(`${API_BASE_URL}/v1/facilities`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const blob = await res.blob();       // .p12
      const url  = window.URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = `${formData.emailAddress || 'certificate'}.p12`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      setSuccess('Facility registered – certificate downloaded!');
    } catch (err) {
      console.error(err);
      setError('Submission failed. Please try again.');
    }
  };

  /* ---------- UI ---------- */
  return (
    <Container maxWidth="sm" sx={{ mt: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h4" gutterBottom>Register Your Facility</Typography>

        {error   && <Alert severity="error"   sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

        <Box component="form" onSubmit={handleSubmit} noValidate>
          <Grid container spacing={2}>
            {/* --- inputs identiques à la version précédente --- */}
            <Grid item xs={12}>
              <TextField required fullWidth label="Facility Name"
                name="name" value={formData.name} onChange={handleChange}/>
            </Grid>
            <Grid item xs={12}>
              <TextField required fullWidth label="Address"
                name="address" value={formData.address} onChange={handleChange}/>
            </Grid>
            <Grid item xs={6}>
              <TextField disabled fullWidth label="Longitude"
                name="lon" value={formData.lon}/>
            </Grid>
            <Grid item xs={6}>
              <TextField disabled fullWidth label="Latitude"
                name="lat" value={formData.lat}/>
            </Grid>
            <Grid item xs={12}>
              <TextField select fullWidth label="Type" name="type"
                value={formData.type} onChange={handleChange}>
                <MenuItem value="cron">cron</MenuItem>
                <MenuItem value="intent">intent</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField required fullWidth label="City / Locality (L)"
                name="L" value={formData.L} onChange={handleChange}/>
            </Grid>
            <Grid item xs={12}>
              <TextField required fullWidth label="Country Code (C)"
                name="C" inputProps={{ maxLength: 2 }}
                value={formData.C} onChange={handleChange}/>
                <Grid item xs={12}>
  <TextField select fullWidth label="Facility Type" name="facility_type"
    value={formData.facility_type || 'other'} onChange={handleChange}>
    <MenuItem value="restaurant">Restaurant</MenuItem>
    <MenuItem value="gite">Gîte</MenuItem>
    <MenuItem value="bar">Bar</MenuItem>
    <MenuItem value="hotel">Hôtel</MenuItem>
    <MenuItem value="museum">Musée</MenuItem>
    <MenuItem value="shop">Boutique</MenuItem>
    <MenuItem value="pharmacy">Pharmacie</MenuItem>
    <MenuItem value="bakery">Boulangerie</MenuItem>
    <MenuItem value="activity">Activité</MenuItem>
    <MenuItem value="camping">Camping</MenuItem>
    <MenuItem value="other">Autre</MenuItem>
  </TextField>
</Grid>

            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Organizational Unit (OU)"
                name="OU" value={formData.OU} onChange={handleChange}/>
            </Grid>
            <Grid item xs={12}>
              <TextField required fullWidth type="email" label="Email Address"
                name="emailAddress" value={formData.emailAddress} onChange={handleChange}/>
            </Grid>
            <Grid item xs={12}>
              <TextField required fullWidth type="password" label="Password"
                name="password" value={formData.password} onChange={handleChange}/>
            </Grid>
          </Grid>

          <input type="hidden" name="evaluationDays" value={formData.evaluationDays} />

          <Button type="submit" variant="contained" sx={{ mt: 3 }} fullWidth>
            Submit &amp; Download Certificate
          </Button>
        </Box>
      </Paper>
    </Container>
  );
}
